"use client";
import { useAtom, useAtomValue } from "jotai";
import { useEffect } from "react";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { CrossSellViewMode } from "@/api/AppApi/services/company/interfaces/ICompany";
import {
  goToCartAtom,
  goToCartOpenCountAtom,
} from "@/app/components/pages-components/common/go-to-cart-pop-up/goToCartAtom";
import { crossSellPopupOpenAtom } from "@/hooks/cartAtoms";
import CrossSellAddMoment from "./CrossSellAddMoment";

/**
 * Owns cross-sell popup mode, app-wide.
 *
 * The trigger is adding a product — wherever that happens — not visiting the
 * cart, so this is mounted once in the layout rather than on the cart page. It
 * reuses `goToCartAtom`, which every add-to-cart path already raises, so there
 * is exactly one "something was added" signal in the app.
 *
 * When the store also has the add-to-cart confirmation popup switched on, the
 * two are merged into a single modal instead of stacking: the confirmation is
 * its header, the recommendations are its body.
 */
const CartCrossSellPopupHost: React.FC = () => {
  const [addedToCart, setAddedToCart] = useAtom(goToCartAtom);
  const openCount = useAtomValue(goToCartOpenCountAtom);
  const [, setCrossSellOwnsMoment] = useAtom(crossSellPopupOpenAtom);

  const { data: companyData } = useGetCompanyQuery();
  const components = companyData?.result?.theme?.components;
  const cartSettings = components?.cart;
  const confirmsAdd = Boolean(components?.product?.show_go_to_cart_popup);
  const ownsMoment =
    Boolean(cartSettings?.show_crosssell_products) &&
    cartSettings?.crosssell_view_mode === CrossSellViewMode.popup;

  // Tell GoToCartPopUp to stand down for the whole add-to-cart moment, not just
  // while our popup is painted — otherwise it flashes in the gap before the
  // recommendations resolve.
  useEffect(() => {
    setCrossSellOwnsMoment(ownsMoment && addedToCart);
    return () => setCrossSellOwnsMoment(false);
  }, [ownsMoment, addedToCart, setCrossSellOwnsMoment]);

  if (!ownsMoment || openCount === 0) return null;

  // Mounted on the first add and kept alive after dismissal so the popup can
  // animate out — gating on `addedToCart` instead would rip it off screen.
  // Keyed on the open count so each *new* add starts a fresh moment: the basket
  // is re-read and the recommendations re-scoped, with nothing carried over.
  return (
    <CrossSellAddMoment
      key={openCount}
      show={addedToCart}
      confirmsAdd={confirmsAdd}
      onClose={() => setAddedToCart(false)}
    />
  );
};

export default CartCrossSellPopupHost;
